import React, { useRef, useState, useEffect } from 'react'
import cn from 'classnames'

import Button from 'js/components/Button/Button'
import jackie from './jackie.jpg'
import karoline from './karoline.jpg'
import ryan from './ryan.jpg'
import banner from './banner.jpg'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import Logo from 'js/components/Logo'
import { FormattedDate, FormattedMessage, IntlProvider } from 'react-intl'
import withSSR from 'js/components/withSSR/withSSR'
import { runQuery } from 'js/dato'
import distanceInSeconds from 'date-fns/differenceInSeconds'

import image1 from './image-1.jpg'
import image2 from './image-2.jpg'
import image3 from './image-3.jpg'
import image4 from './image-4.jpg'

import image1Uk from './image-1-uk.jpg'
import image2Uk from './image-2-uk.jpg'
import image3Uk from './image-3-uk.jpg'
import image4Uk from './image-4-uk.jpg'

import WebinarVideos from './WebinarVideos'
import CountdownPage from './CountdownPage'
import Subscribe from './Subscribe'
import { setItem, getItem } from 'js/storeManager'
import Modal from 'js/components/Modal/Modal'
import messages from './messages'
import { WebinarQuery_allWebinars } from './types/WebinarQuery'

function checkEmail(email) {
  return fetch(
    `https://cool-thunder-ba27.lifesum.workers.dev/?email=${email}`,
  ).then((response) => response.json())
}

type AlreadyRegisteredButtonProps = {
  onClick: (event: React.SyntheticEvent) => void
}

function AlreadyRegisteredButton({ onClick }: AlreadyRegisteredButtonProps) {
  return (
    <button className="cursor-pointer text-grey-dark" onClick={onClick}>
      <FormattedMessage
        id="webinar_already_registred"
        values={{
          span: function Underline(...chunks) {
            return <span className="underline">{chunks}</span>
          },
        }}
      />
    </button>
  )
}

type DateBoxProps = {
  value: string
  index: number
}

function DateBox({ value, index }: DateBoxProps) {
  const active = Date.now() - new Date(value).getTime() >= 0

  return (
    <Link
      to={`../videos/${index + 1}`}
      className={!active ? 'pointer-events-none' : ''}
    >
      <div
        className="group flex flex-col items-center justify-end rounded-20 bg-white pb-12 text-center shadow-subtle"
        style={{
          width: 148,
          height: 148,
        }}
      >
        <span
          className={cn(
            'type-80-bold ',
            active
              ? 'cursor-pointer text-grey-darkest group-hover:text-black'
              : 'cursor-default text-grey-light',
          )}
          style={{ lineHeight: 1, letterSpacing: 0.8 }}
        >
          <FormattedDate value={value} day="2-digit" />
        </span>
        <span
          className={cn(
            'tracking-heading type-24-bold',
            active ? 'text-green' : 'text-grey-lightest',
          )}
          style={{ lineHeight: 1 }}
        >
          <span className="uppercase">
            <FormattedDate value={value} month="short" />
          </span>
        </span>
      </div>
    </Link>
  )
}

type ExpertProps = {
  image: string
  name: string
  text: React.ReactNode
  handle: string
}

function Expert({ image, name, text, handle }: ExpertProps) {
  return (
    <div className="mx-12 rounded-20 border-Border py-36 lg:border">
      <img
        src={image}
        alt=""
        className="mb-20 rounded-full object-cover"
        width="172"
        height="172"
        style={{ height: 172, width: 172 }}
      />
      <div className="mb-4 type-24-demibold">{name}</div>
      <div className="mb-8 text-green type-17-demibold">
        <a
          href={`https://www.instagram.com/${handle}`}
          className="text-green"
          target="_blank"
          rel="noreferrer"
        >
          @{handle}
        </a>
      </div>
      <div className="mx-auto max-w-512 leading-body type-20">{text}</div>
    </div>
  )
}

type WebinarStartPageProps = {
  webinars: Array<WebinarQuery_allWebinars>
  subscribedEmail: string | null
  onSubscribed: (email: string) => void
}

function WebinarStartPage({
  webinars,
  subscribedEmail,
  onSubscribed,
}: WebinarStartPageProps) {
  const subscribeRef = useRef<HTMLParagraphElement>(null)
  const { pathname } = useLocation()
  const basePath = pathname.replace('*', '')
  const [showModal, setShowModal] = useState(false)

  const isUk = pathname.includes('webinars-uk')
  const locale = isUk ? 'en' : 'sv'

  return (
    <IntlProvider locale={locale} messages={messages[locale]}>
      <div className="h-screen">
        <Routes>
          <Route
            path=""
            element={
              <div className="bg-Bg">
                <div
                  className="bg-BgSub bg-cover pb-56 pt-80 text-center md:pb-72 md:pt-128 lg:pb-96 xl:pb-128"
                  style={{
                    background: `linear-gradient(162deg,rgba(19, 20, 20, 0.4),rgba(19, 20, 20, 0.9)), url(${banner})`,
                  }}
                >
                  <div className="o-width-limiter">
                    <h1 className="mb-8 text-white type-40-bold md:type-48-bold xl:pt-40">
                      <FormattedMessage id="webinar_heading" />
                    </h1>
                    <div
                      className={
                        'mx-auto max-w-576 leading-body text-white type-20 md:type-24 lg:max-w-768 xxl:type-28'
                      }
                    >
                      <FormattedMessage id="webinar_subheading" />
                    </div>
                  </div>
                </div>
                {!subscribedEmail && (
                  <div
                    className="mx-auto -translate-y-1/2 transform text-center"
                    style={{ maxWidth: 300 }}
                  >
                    <Button
                      big
                      color="premium"
                      use="a"
                      onClick={() => {
                        subscribeRef.current &&
                          subscribeRef.current.scrollIntoView({
                            behavior: 'smooth',
                          })
                      }}
                    >
                      <FormattedMessage id="webinar_cta" />
                    </Button>
                  </div>
                )}
                <div
                  className={`
                mx-auto my-48 
                max-w-576 bg-white px-20
                text-center leading-body lg:max-w-768 `}
                >
                  <h2 className="type-large-fat mb-36">
                    <FormattedMessage id="webinar_info_heading" />
                  </h2>
                  <h3 className="mb-12 type-20-demibold ">
                    <FormattedMessage id="webinar_info_callout" />
                  </h3>
                  <p className="mb-12 md:mb-24" ref={subscribeRef}>
                    <FormattedMessage id="webinar_info_1" />
                  </p>
                  <p className="mb-12 md:mb-24">
                    <FormattedMessage id="webinar_info_2" />
                  </p>
                  {!subscribedEmail && (
                    <p className="mb-36 md:mb-24">
                      <FormattedMessage id="webinar_register_now" />
                    </p>
                  )}

                  <div className="mx-auto my-36 flex max-w-480 justify-center rounded-12 border border-Border  bg-white p-36 text-center">
                    <Subscribe
                      isSubscribed={subscribedEmail}
                      onSubscribed={onSubscribed}
                      alreadyRegisteredButton={
                        <AlreadyRegisteredButton
                          onClick={() => setShowModal(true)}
                        />
                      }
                    />
                  </div>
                </div>
                <div
                  className={`
              mx-auto flex max-w-968 
              flex-col items-center bg-BgSub px-20 
              pb-48 pt-36 text-center lg:my-64 lg:rounded-20`}
                >
                  <h3 className="mb-24 text-grey-dark type-24-bold">
                    <FormattedMessage id="webinar_date_heading" />
                  </h3>
                  <div className="mb-36 flex max-w-screen justify-center overflow-hidden">
                    <div className="flex overflow-x-scroll lg:overflow-x-auto">
                      {webinars.map((webinar, index) => (
                        <div className="m-8 first:pl-48 last:pr-48" key={index}>
                          <DateBox
                            value={webinar.publishDateTime}
                            index={index}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  {subscribedEmail ? (
                    <Button use={Link} outline to={`${basePath}/videos`}>
                      <FormattedMessage id="webinar_link_to_all" />
                    </Button>
                  ) : (
                    <>
                      <div className="mb-12 flex flex-col font-demibold">
                        <span className="mb-16">
                          <FormattedMessage id="webinar_register_to_see" />
                        </span>
                        <Button
                          outline
                          onClick={() => {
                            subscribeRef.current &&
                              subscribeRef.current.scrollIntoView({
                                behavior: 'smooth',
                              })
                          }}
                        >
                          <FormattedMessage id="webinar_register" />
                        </Button>
                      </div>
                      <AlreadyRegisteredButton
                        onClick={() => setShowModal(true)}
                      />
                    </>
                  )}
                </div>

                <div className="mx-auto max-w-968 bg-white px-20 py-36 text-center">
                  <div className="grid grid-cols-2 gap-2">
                    {(isUk
                      ? [image1Uk, image2Uk, image3Uk, image4Uk]
                      : [image1, image2, image3, image4]
                    ).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt=""
                        className="rounded-12 object-cover"
                        style={{
                          height: '30vw',
                          maxHeight: 300,
                          width: '100%',
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="mx-auto max-w-968 bg-white px-20 py-72 pb-72 text-center">
                  <h2 className="type-large-fat pb-36">
                    <FormattedMessage id="webinar_who_are_we" />
                  </h2>
                  <div className="flex flex-col justify-center lg:flex-row">
                    {locale === 'en' ? (
                      <Expert
                        image={ryan}
                        handle="livevitae"
                        name="Ryan Carter"
                        text={<FormattedMessage id="webinar_ryan" />}
                      />
                    ) : (
                      <>
                        <Expert
                          image={jackie}
                          handle="jackiemasete"
                          name="Jackie Masete"
                          text={<FormattedMessage id="webinar_jackie" />}
                        />
                        <Expert
                          image={karoline}
                          handle="karolineepettersson"
                          name="Karoline Pettersson"
                          text={<FormattedMessage id="webinar_kp" />}
                        />
                      </>
                    )}
                  </div>
                </div>
                {!subscribedEmail && (
                  <div className="flex justify-center">
                    <Button
                      big
                      color="premium"
                      className="translate-y-1/2 transform"
                      use="a"
                      onClick={() => {
                        subscribeRef.current &&
                          subscribeRef.current.scrollIntoView({
                            behavior: 'smooth',
                          })
                      }}
                      nudge={false}
                    >
                      <FormattedMessage id="webinar_cta" />
                    </Button>
                  </div>
                )}

                <div className="flex justify-center bg-BgSub pb-72 pt-96">
                  <Logo />
                </div>
                {showModal && (
                  <Modal onClose={() => setShowModal(false)}>
                    <Subscribe
                      verify
                      onSubscribed={(email) => {
                        setShowModal(false)
                        onSubscribed(email)
                      }}
                    />
                  </Modal>
                )}
              </div>
            }
          />

          <Route
            path="videos"
            element={
              subscribedEmail ? (
                <WebinarVideos webinars={webinars} />
              ) : (
                <Modal onClose={() => setShowModal(false)}>
                  <Subscribe
                    verify
                    onSubscribed={(email) => {
                      setShowModal(false)
                      onSubscribed(email)
                    }}
                  />
                </Modal>
              )
            }
          />
          <Route
            path="videos/:video"
            element={
              subscribedEmail ? (
                <WebinarVideos webinars={webinars} />
              ) : (
                <Modal onClose={() => setShowModal(false)}>
                  <Subscribe
                    verify
                    onSubscribed={(email) => {
                      setShowModal(false)
                      onSubscribed(email)
                    }}
                  />
                </Modal>
              )
            }
          />
        </Routes>
      </div>
    </IntlProvider>
  )
}

type Props = {
  data: Array<WebinarQuery_allWebinars>
  isLoading: boolean
}

function WebinarPage({ data: webinars, isLoading }: Props) {
  const { search } = useLocation()
  const email = new URLSearchParams(search).get('email')
  const { pathname } = useLocation()

  const skipCountdown = pathname.includes('webinars-test')
  const locale = pathname.includes('webinars-uk') ? 'en' : 'sv'

  const [subscribedEmail, setSubscribedEmail] = useState(
    getItem('webinar-subscribe-email') || null,
  )

  useEffect(() => {
    const persistedEmail = getItem('webinar-subscribe-email') || null
    if (persistedEmail) return setSubscribedEmail(persistedEmail)

    if (!email) return
    checkEmail(email).then(({ email }) => {
      if (email) {
        setSubscribedEmail(email)
        setItem('webinar-subscribe-email', email)
      }
    })
  }, [email])

  if (isLoading) return null

  const firstWebinarDate = webinars[0].publishDateTime
  const showCountDown =
    distanceInSeconds(Date.now(), new Date(firstWebinarDate)) < 0

  if (!skipCountdown && showCountDown)
    return (
      <IntlProvider locale={locale} messages={messages[locale]}>
        <CountdownPage
          subscribedEmail={subscribedEmail}
          target={firstWebinarDate}
        />
      </IntlProvider>
    )

  return (
    <WebinarStartPage
      webinars={webinars}
      subscribedEmail={subscribedEmail}
      onSubscribed={(email) => {
        setSubscribedEmail(email)
        setItem('webinar-subscribe-email', email)
      }}
    />
  )
}

WebinarPage.getInitialProps = ({ location }) => {
  const country = location.pathname.includes('webinars-uk') ? 'uk' : 'se'

  return runQuery(
    gql`
      query WebinarQuery($country: String!) {
        allWebinars(
          orderBy: publishDate_ASC
          filter: { country: { eq: $country } }
        ) {
          id
          publishDate
          publishDateTime
          title
          video {
            id: providerUid
            title
            url
            thumbnailUrl
            height
            width
          }
          description
        }
      }
    `,
    { country },
  ).then(({ allWebinars }) => allWebinars as Array<WebinarQuery_allWebinars>)
}

export default withSSR(WebinarPage)
